import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraButton, SpecraInput, SpecraSelect } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useProjectVolumes, useRenameVolume } from '../../../../hooks/useProjectVolumes';
import { useCreateResponseArtifact, useReorderProposalVolumes } from './responseStructureHooks';
/** Working response artifacts are independent of extraction and compliance completion. */ export function ResponseStructurePanel({ projectId }) {
    const volumes = useProjectVolumes(projectId);
    const create = useCreateResponseArtifact(projectId);
    const reorder = useReorderProposalVolumes(projectId);
    const ordering = useRef(false);
    const [orderPending, setOrderPending] = useState(false);
    const [orderError, setOrderError] = useState('');
    const [retryMove, setRetryMove] = useState(null);
    const move = async (id, direction, anchorId)=>{
        if (ordering.current || create.isPending) return;
        ordering.current = true;
        setOrderPending(true);
        setOrderError('');
        try {
            const current = anchorId ? await volumes.refetch() : {
                data: volumes.data
            };
            if (!current.data || 'isError' in current && current.isError) throw new Error('Reload failed');
            const ids = current.data.map((volume)=>volume.volumeId);
            const index = ids.indexOf(id);
            const target = anchorId ?? ids[index + direction];
            if (index < 0 || !target || !ids.includes(target)) {
                setRetryMove(null);
                setOrderError('The volumes changed. Choose a new move from the refreshed list.');
                return;
            }
            setRetryMove({
                id,
                direction,
                anchorId: target
            });
            ids.splice(index, 1);
            const targetIndex = ids.indexOf(target);
            ids.splice(targetIndex + (direction > 0 ? 1 : 0), 0, id);
            await reorder.mutateAsync(ids);
            setRetryMove(null);
        } catch  {
            setOrderError('Unable to save volume order. The list shows the saved order. Retry reloads current volumes before moving.');
        } finally{
            ordering.current = false;
            setOrderPending(false);
        }
    };
    const [name, setName] = useState('');
    const [kind, setKind] = useState('prose');
    const [error, setError] = useState('');
    const submitting = useRef(false);
    return /*#__PURE__*/ _jsxs("section", {
        "aria-label": "Response structure",
        "data-testid": Testids.proposalStructure.panel,
        className: "space-y-3 rounded-lg border border-border bg-card p-4 text-foreground",
        children: [
            /*#__PURE__*/ _jsx("h2", {
                className: "font-semibold",
                children: "Response structure"
            }),
            /*#__PURE__*/ _jsx("p", {
                className: "text-muted-foreground",
                children: "Build the solicitation’s actual response. Start writing before requirement mapping is complete. Templates change presentation; the solicitation remains the source of authority."
            }),
            volumes.isLoading ? /*#__PURE__*/ _jsx("output", {
                children: "Loading response structure…"
            }) : volumes.isError ? /*#__PURE__*/ _jsxs("div", {
                role: "alert",
                children: [
                    "Unable to load volumes.",
                    ' ',
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        variant: "secondary",
                        onClick: ()=>volumes.refetch(),
                        children: "Try again"
                    })
                ]
            }) : /*#__PURE__*/ _jsx("ul", {
                className: "space-y-2",
                children: (volumes.data ?? []).map((volume, index, all)=>/*#__PURE__*/ _jsx(ArtifactRow, {
                        projectId: projectId,
                        volume: volume,
                        ordering: orderPending || create.isPending || volumes.isFetching,
                        first: index === 0,
                        last: index === all.length - 1,
                        onMove: (direction)=>void move(volume.volumeId, direction)
                    }, volume.volumeId))
            }),
            orderPending && /*#__PURE__*/ _jsx("output", {
                children: "Saving volume order…"
            }),
            orderError && /*#__PURE__*/ _jsxs("div", {
                role: "alert",
                children: [
                    orderError,
                    retryMove && /*#__PURE__*/ _jsx(SpecraButton, {
                        "data-testid": Testids.proposalStructure.retryVolumeMove,
                        disabled: orderPending,
                        onClick: ()=>void move(retryMove.id, retryMove.direction, retryMove.anchorId),
                        children: "Retry volume move"
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("fieldset", {
                disabled: orderPending,
                children: /*#__PURE__*/ _jsxs("form", {
                    className: "flex flex-col gap-2 sm:flex-row",
                    onSubmit: async (event)=>{
                        event.preventDefault();
                        if (submitting.current || ordering.current || !name.trim()) return;
                        submitting.current = true;
                        setError('');
                        try {
                            await create.mutateAsync({
                                displayName: name.trim(),
                                kind
                            });
                            setName('');
                        } catch (err) {
                            setError(err instanceof Error ? err.message : 'Unable to create the volume. Try again.');
                        } finally{
                            submitting.current = false;
                        }
                    },
                    children: [
                        /*#__PURE__*/ _jsx(SpecraInput, {
                            "aria-label": "Volume name",
                            "data-testid": Testids.proposalStructure.name,
                            value: name,
                            maxLength: 200,
                            required: true,
                            disabled: create.isPending,
                            onChange: (event)=>setName(event.target.value)
                        }),
                        /*#__PURE__*/ _jsxs(SpecraSelect, {
                            "aria-label": "Response artifact type",
                            "data-testid": Testids.proposalStructure.kind,
                            value: kind,
                            disabled: create.isPending,
                            onChange: (event)=>setKind(event.target.value),
                            children: [
                                /*#__PURE__*/ _jsx("option", {
                                    value: "prose",
                                    children: "Written response"
                                }),
                                /*#__PURE__*/ _jsx("option", {
                                    value: "workbook",
                                    children: "Pricing / government workbook"
                                }),
                                /*#__PURE__*/ _jsx("option", {
                                    value: "template",
                                    children: "Government form / requested attachment"
                                }),
                                /*#__PURE__*/ _jsx("option", {
                                    value: "presentation",
                                    children: "Presentation"
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsx(SpecraButton, {
                            type: "submit",
                            "data-testid": Testids.proposalStructure.create,
                            isLoading: create.isPending,
                            disabled: !name.trim(),
                            children: "Add volume"
                        })
                    ]
                })
            }),
            error && /*#__PURE__*/ _jsx("p", {
                role: "alert",
                children: error
            })
        ]
    });
}
function ArtifactRow({ projectId, volume, ordering, first, last, onMove }) {
    const rename = useRenameVolume();
    const [editing, setEditing] = useState(false);
    const [name, setName] = useState(volume.displayName);
    const [error, setError] = useState('');
    const submitting = useRef(false);
    return /*#__PURE__*/ _jsxs("li", {
        "data-testid": Testids.proposalStructure.row,
        "data-volume-id": volume.volumeId,
        className: "space-y-2",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-center justify-between gap-2",
                children: [
                    /*#__PURE__*/ _jsx(Link, {
                        to: `/proposal/${projectId}/v2/volume/${volume.volumeId}`,
                        className: "text-brand hover:underline",
                        children: volume.displayName
                    }),
                    /*#__PURE__*/ _jsxs("span", {
                        className: "text-muted-foreground",
                        children: [
                            volume.kind || 'prose',
                            " · ",
                            volume.isCustom ? 'Custom' : 'Source-derived'
                        ]
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        variant: "ghost",
                        "data-testid": Testids.proposalStructure.moveVolumeUp,
                        "aria-label": `Move ${volume.displayName} up`,
                        disabled: ordering || first,
                        onClick: ()=>onMove(-1),
                        children: "Move up"
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        variant: "ghost",
                        "data-testid": Testids.proposalStructure.moveVolumeDown,
                        "aria-label": `Move ${volume.displayName} down`,
                        disabled: ordering || last,
                        onClick: ()=>onMove(1),
                        children: "Move down"
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        variant: "ghost",
                        onClick: ()=>{
                            setName(volume.displayName);
                            setEditing(!editing);
                        },
                        disabled: rename.isPending,
                        children: "Rename"
                    })
                ]
            }),
            editing && /*#__PURE__*/ _jsxs("form", {
                className: "flex gap-2",
                onSubmit: async (event)=>{
                    event.preventDefault();
                    if (submitting.current || !name.trim()) return;
                    submitting.current = true;
                    setError('');
                    try {
                        await rename.mutateAsync({
                            projectId,
                            volumeId: volume.volumeId,
                            displayName: name.trim()
                        });
                        setEditing(false);
                    } catch (err) {
                        setError(err instanceof Error ? err.message : 'Unable to rename this volume. Try again.');
                    } finally{
                        submitting.current = false;
                    }
                },
                children: [
                    /*#__PURE__*/ _jsx(SpecraInput, {
                        "aria-label": `Rename ${volume.displayName}`,
                        value: name,
                        maxLength: 200,
                        disabled: rename.isPending,
                        required: true,
                        onChange: (event)=>setName(event.target.value)
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        type: "submit",
                        isLoading: rename.isPending,
                        disabled: !name.trim(),
                        children: "Save name"
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        variant: "secondary",
                        onClick: ()=>setEditing(false),
                        disabled: rename.isPending,
                        children: "Cancel"
                    })
                ]
            }),
            error && /*#__PURE__*/ _jsx("p", {
                role: "alert",
                children: error
            })
        ]
    });
}
